import type {Loan, Pageable, ProvidedSeeking, SeekedSeeking, Seeking, Valuable} from 'typings/portaro.be.types';
import type {AjaxService} from 'core/data-services/ajax.service';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type {UserId} from 'src/features/user/types';

export default class ReaderLoanDataService {
    public static readonly serviceName = 'readerLoanDataService';

    public static readonly LOAN_ITEMS_ROUTE = 'loan-items';
    public static readonly LOAN_ITEMS_SIZE_ROUTE = 'loan-items/size';

    /*@ngInject*/
    constructor(private ajaxService: AjaxService) {
    }

    public async getAllLoans(userId: UserId, pageable?: Pageable): Promise<Loan[]> {
        return this.getLoans(userId, pageable, 'loan');
    }

    public async getAllWaitingLoans(userId: UserId, pageable?: Pageable): Promise<Loan[]> {
        return this.getLoans(userId, pageable, 'waiting-loan');
    }

    public async getAllEndedLoans(userId: UserId, pageable?: Pageable): Promise<Loan[]> {
        return this.getLoans(userId, pageable, 'ended-loan');
    }

    public async getAllIllSeekingsOfRequester(userId: UserId, pageable?: Pageable): Promise<Seeking[]> {
        return this.getLoans(userId, pageable, 'seeking-of-requester');
    }

    public async getAllIllSeekingsOfProvider(userId: UserId, pageable?: Pageable): Promise<SeekedSeeking[]> {
        return this.getLoans(userId, pageable, 'seeking-of-provider');
    }

    public async getAllIllProvidedSeekingsOfSeeker(userId: UserId, pageable?: Pageable): Promise<ProvidedSeeking[]> {
        return this.getLoans(userId, pageable, 'seeking-of-seeker');
    }

    public async getAllLoansSize(userId: UserId): Promise<Valuable<number>> {
        return this.getLoanSize(userId, 'loan');
    }

    public async getAllWaitingLoansSize(userId: UserId): Promise<Valuable<number>> {
        return this.getLoanSize(userId, 'waiting-loan');
    }

    public async getAllEndedLoansSize(userId: UserId): Promise<Valuable<number>> {
        return this.getLoanSize(userId, 'ended-loan');
    }

    public async getAllIllSeekingsOfRequesterSize(userId: UserId): Promise<Valuable<number>> {
        return this.getLoanSize(userId, 'seeking-of-requester');
    }

    public async getAllIllProvidedSeekingsOfSeekerSize(userId: UserId): Promise<Valuable<number>> {
        return this.getLoanSize(userId, 'seeking-of-seeker');
    }

    public async getAllIllSeekingsOfProviderSize(userId: UserId): Promise<Valuable<number>> {
        return this.getLoanSize(userId, 'seeking-of-provider');
    }

    private async getLoans(userId: UserId, pageable: Pageable, type: LoanType): Promise<Loan[]>
    private async getLoans(userId: UserId, pageable: Pageable, type: 'seeking-of-requester'): Promise<Seeking[]>
    private async getLoans(userId: UserId, pageable: Pageable, type: 'seeking-of-provider'): Promise<SeekedSeeking[]>
    private async getLoans(userId: UserId, pageable: Pageable, type: 'seeking-of-seeker'): Promise<ProvidedSeeking[]>

    @ngAsync()
    private async getLoans(userId: UserId, pageable: Pageable, type: SeekingType | LoanType): Promise<Loan[] | Seeking[] | SeekedSeeking[] | ProvidedSeeking[]> {
        let queryParams = {user: userId, types: type};
        queryParams = pageable ? Object.assign({}, queryParams, pageable) : queryParams;

        return this.ajaxService
            .createRequest(`${ReaderLoanDataService.LOAN_ITEMS_ROUTE}`)
            .get(queryParams);
    }

    @ngAsync()
    private async getLoanSize(userId: UserId, type: string): Promise<Valuable<number>> {
        return this.ajaxService
            .createRequest(`${ReaderLoanDataService.LOAN_ITEMS_SIZE_ROUTE}`)
            .get({user: userId, types: type});
    }
}

type SeekingType = 'seeking-of-requester'
    | 'seeking-of-provider'
    | 'seeking-of-seeker'

type LoanType = 'loan'
    | 'ended-loan'
    | 'waiting-loan'